// =============================================================================
// Module Core - Repository: Role
// Accès DB pour les rôles et permissions
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Role, Permission, RoleWithPermissions, MemberRole, AppRole } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ALL ROLES WITH PERMISSIONS (bulk)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchAllRolesWithPermissions(orgId: string): Promise<RoleWithPermissions[]> {
  const { data, error } = await supabase
    .from('core_roles')
    .select(`
      *,
      role_permissions:core_role_permissions(
        permission:core_permissions(*)
      )
    `)
    .eq('org_id', orgId)
    .order('code');

  if (error) throw error;

  return (data || []).map((role: any) => ({
    ...role,
    permissions: role.role_permissions?.map((rp: any) => rp.permission).filter(Boolean) || [],
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ROLES FOR ORGANIZATION
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchOrganizationRoles(orgId: string): Promise<Role[]> {
  const { data, error } = await supabase
    .from('core_roles')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching organization roles:', error);
    return [];
  }

  return data as Role[];
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ROLE WITH PERMISSIONS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchRoleWithPermissions(roleId: string): Promise<RoleWithPermissions | null> {
  const { data: roleData, error: roleError } = await supabase
    .from('core_roles')
    .select('*')
    .eq('id', roleId)
    .single();

  if (roleError) {
    console.error('Error fetching role:', roleError);
    return null;
  }

  const { data: permissionsData, error: permError } = await supabase
    .from('core_role_permissions')
    .select(`
      core_permissions (
        id,
        code,
        name,
        description,
        module,
        created_at
      )
    `)
    .eq('role_id', roleId);

  if (permError) {
    console.error('Error fetching role permissions:', permError);
    return { ...(roleData as Role), permissions: [] };
  }

  const permissions = (permissionsData || [])
    .map((row) => row.core_permissions as unknown as Permission)
    .filter((p): p is Permission => p !== null);

  return { ...(roleData as Role), permissions };
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ALL PERMISSIONS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchAllPermissions(): Promise<Permission[]> {
  const { data, error } = await supabase
    .from('core_permissions')
    .select('*')
    .order('module', { ascending: true });

  if (error) {
    console.error('Error fetching permissions:', error);
    return [];
  }

  return data as Permission[];
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH MEMBER ROLES
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchMemberRoles(memberId: string): Promise<Role[]> {
  const { data, error } = await supabase
    .from('core_member_roles')
    .select(`
      core_roles (
        id,
        org_id,
        code,
        name,
        description,
        is_system,
        created_at,
        updated_at
      )
    `)
    .eq('member_id', memberId);

  if (error) {
    console.error('Error fetching member roles:', error);
    return [];
  }

  return (data || [])
    .map((row) => row.core_roles as unknown as Role)
    .filter((r): r is Role => r !== null);
}

// ─────────────────────────────────────────────────────────────────────────────
// ASSIGN ROLE TO MEMBER (via Edge Function)
// ─────────────────────────────────────────────────────────────────────────────
export async function assignRoleToMember(
  orgId: string,
  memberId: string,
  roleCode: AppRole
): Promise<boolean> {
  const { error } = await supabase.functions.invoke('assign-role', {
    body: { org_id: orgId, member_id: memberId, role_code: roleCode },
  });

  if (error) {
    console.error('Error assigning role:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// CHANGE MEMBER ROLE (via Edge Function)
// ─────────────────────────────────────────────────────────────────────────────
export async function changeMemberRole(
  orgId: string,
  memberId: string,
  newRoleCode: AppRole
): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('change-member-role', {
    body: { org_id: orgId, member_id: memberId, new_role_code: newRoleCode },
  });

  if (error) {
    console.error('Error changing member role:', error);
    return { success: false, error: error.message };
  }

  if (data?.error) {
    return { success: false, error: data.error };
  }

  return { success: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// GET USER ROLE IN ORGANIZATION
// ─────────────────────────────────────────────────────────────────────────────
export async function getUserRole(orgId: string): Promise<string | null> {
  // Try RPC first
  const { data, error } = await supabase.rpc('get_user_role', { _org_id: orgId });

  if (!error && data) {
    return data as string;
  }

  if (error) {
    console.warn('RPC get_user_role failed, falling back to direct query:', error.message);
  }

  // Fallback: query directly if RPC fails (e.g., function not deployed on staging)
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: memberData } = await supabase
      .from('core_organization_members')
      .select('id')
      .eq('org_id', orgId)
      .eq('user_id', user.id)
      .eq('status', 'active')
      .single();

    if (!memberData) return null;

    const { data: roleData } = await supabase
      .from('core_member_roles')
      .select('core_roles!inner(code)')
      .eq('member_id', memberData.id)
      .limit(1)
      .single();

    return (roleData as any)?.core_roles?.code || null;
  } catch (fallbackErr) {
    console.error('Fallback role query failed:', fallbackErr);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECK IF USER HAS PERMISSION
// ─────────────────────────────────────────────────────────────────────────────
export async function hasPermission(orgId: string, permissionCode: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('has_permission', {
    _org_id: orgId,
    _permission_code: permissionCode,
  });

  if (error) {
    console.error('Error checking permission:', error);
    return false;
  }

  return data === true;
}
